/**
 * DevToolsHeader — the DevToolsPanel chrome: the 6-tab strip (adopting the
 * shared TabBar primitive, `underline` variant to keep the accent-underline
 * active look) doubling as the drag handle, plus the dev-action-log toggle,
 * console-log-mode toggle and collapse button. Extracted from DevToolsPanel
 * during the decomposition; behavior-preserving.
 */
import React from 'react';
import { TabBar, type TabDescriptor } from '../../primitives';
import { RAW } from '../../theme';
import {
  headerBarStyle,
  tabContainerStyle,
  toggleButtonStyle,
  collapseButtonStyle,
} from './devToolsPanelStyles';

export type DevToolsTab =
  | 'player'
  | 'physics'
  | 'classes'
  | 'npcs'
  | 'projectiles'
  | 'connections'
  | 'featureManager';

/** Tab order + icons preserved 1:1 from the legacy hand-rolled strip. */
const TABS: TabDescriptor[] = [
  { id: 'player', label: '👤' },
  { id: 'classes', label: '⚔️' },
  { id: 'physics', label: '⚡' },
  { id: 'npcs', label: '🐷' },
  { id: 'projectiles', label: '🚀' },
  { id: 'connections', label: '🔌' },
  { id: 'featureManager', label: '🚦' },
];

const LOG_MODE_LABELS = ['SILENT', 'MINIMAL', 'GAMEPLAY', 'NETWORK', 'VERBOSE'];
const LOG_MODE_ICONS = ['🔇', '💬', '⚔️', '📡', '🔊'];

interface DevToolsHeaderProps {
  activeTab: DevToolsTab;
  showDevActionLogs: boolean;
  logMode: number;
  onTabChange: (tab: DevToolsTab) => void;
  onToggleDevActionLogs: () => void;
  onToggleLogger: () => void;
  // Chrome handlers — OPTIONAL. The engine-mounted version (DevToolsContent
  // inside ToolWindow) omits them: ToolWindow owns drag + minimize, so there's
  // no own drag handle and no collapse button. The legacy custom-chrome version
  // passed them.
  isCollapsed?: boolean;
  onStartDrag?: (e: React.MouseEvent) => void;
  onToggleCollapsed?: () => void;
}

export const DevToolsHeader: React.FC<DevToolsHeaderProps> = ({
  activeTab,
  isCollapsed = false,
  showDevActionLogs,
  logMode,
  onTabChange,
  onStartDrag,
  onToggleDevActionLogs,
  onToggleLogger,
  onToggleCollapsed,
}) => (
  <div style={headerBarStyle}>
    {/* Tab strip doubles as the drag handle */}
    {!isCollapsed && (
      <div style={tabContainerStyle} onMouseDown={onStartDrag}>
        <TabBar
          tabs={TABS}
          activeTab={activeTab}
          onTabChange={(id) => onTabChange(id as DevToolsTab)}
          variant="underline"
        />
      </div>
    )}

    {/* Dev Action Logging toggle */}
    <button
      style={toggleButtonStyle({
        active: showDevActionLogs,
        activeColor: RAW.yellow,
        glowRgba: 'rgba(241, 196, 15, 0.4)',
      })}
      onClick={onToggleDevActionLogs}
      title={showDevActionLogs ? 'Dev Action Logging: ON' : 'Dev Action Logging: OFF'}
    >
      {showDevActionLogs ? '📜' : '📄'}
    </button>

    {/* Console Log mode toggle */}
    <button
      style={toggleButtonStyle({
        active: logMode > 0,
        activeColor: RAW.green,
        glowRgba: 'rgba(46, 204, 113, 0.4)',
      })}
      onClick={onToggleLogger}
      title={`Log Mode: ${LOG_MODE_LABELS[logMode] || 'UNKNOWN'}`}
    >
      {LOG_MODE_ICONS[logMode] || '❓'}
    </button>

    {/* Collapse button — only in the legacy custom-chrome mode; the engine
        version uses ToolWindow's minimize instead. */}
    {onToggleCollapsed && (
      <button style={collapseButtonStyle(isCollapsed)} onClick={onToggleCollapsed}>
        {isCollapsed ? '🛠️' : '−'}
      </button>
    )}
  </div>
);
